import Vue from 'vue'
import { Store } from 'vuex'
import VueRouter from 'vue-router'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { isServer } from '@vue-storefront/core/helpers'
import EventBus from '@vue-storefront/core/compatibility/plugins/event-bus'
import {
  buildEcommercePayload,
  getCartValue,
  getItemListContext,
  mapGa4Item,
  mapGa4Items,
  trackEcommerce
} from '../helpers/ga4'
import { createPageTracker, isProductRoute, normalizePath } from '../helpers/pageTracker'

export const isEnabled = (gtmId: string | null) => {
  return typeof gtmId === 'string' && gtmId.length > 0 && !isServer
}

export function afterRegistration (config, store: Store<any>, router?: VueRouter) {
  if (isEnabled(config.googleTagManager.id) && router) {
    const GTM: any = (Vue as any).gtm

    const storeView = currentStoreView()
    const currencyCode = storeView.i18n.currencyCode || 'EUR'
    const pageTracker = createPageTracker({ GTM, store, router, currencyCode })

    const trackAddToCart = (product, specifiedQty?: number | null) => {
      const item = mapGa4Item(product, store, {}, specifiedQty)
      trackEcommerce(GTM, 'add_to_cart', buildEcommercePayload(currencyCode, [item]))
    }

    const trackRemoveFromCart = (product) => {
      const item = mapGa4Item(product, store)
      trackEcommerce(GTM, 'remove_from_cart', buildEcommercePayload(currencyCode, [item]))
    }

    const trackSelectItem = (clickedProduct) => {
      const listProducts = store.getters['category-next/getCategoryProducts'] || []
      const index = listProducts.findIndex(product => product && product.sku === clickedProduct.sku)
      const product = index >= 0 ? listProducts[index] : clickedProduct
      const listContext = getItemListContext(store)
      const item = mapGa4Item(product, store, {
        ...listContext,
        index: index >= 0 ? index : 0
      })

      trackEcommerce(GTM, 'select_item', buildEcommercePayload(currencyCode, [item], listContext))
    }

    const trackViewCart = (state) => {
      const cartItems = state.cart?.cartItems || []
      const items = mapGa4Items(cartItems, store)
      trackEcommerce(GTM, 'view_cart', buildEcommercePayload(currencyCode, items, {
        value: getCartValue(state, items)
      }))
    }

    EventBus.$on('gtm-select-item', trackSelectItem)

    router.afterEach((to, from) => {
      const samePath = from && from.matched.length && normalizePath(to.path) === normalizePath(from.path)
      if (samePath && isProductRoute(to)) {
        return
      }
      pageTracker.trackRoute(to)
    })

    router.onReady(() => {
      if (router.currentRoute && router.currentRoute.matched.length) {
        pageTracker.trackRoute(router.currentRoute)
      }
    })

    store.subscribe(({ type, payload }, state) => {
      if (type === 'cart/cart/ADD' && state.cart.isAddingToCart) {
        trackAddToCart(payload.product, payload.specifiedQty)
      }

      if (type === 'cart/cart/DEL') {
        trackRemoveFromCart(payload.product)
      }

      if (type === 'product/product/SET_CURRENT' && payload) {
        pageTracker.onProductUpdated()
      }

      if (type === 'category-next/category/SET_PRODUCTS') {
        pageTracker.onCategoryProductsSet(payload || [])
      }

      if (type === 'category-next/category/ADD_PRODUCTS') {
        pageTracker.onCategoryProductsAdd(payload || [])
      }

      if (type === 'ui/setMicrocart' && payload === true) {
        trackViewCart(state)
      }
    })
  }
}
